import { Link } from "react-router-dom";
import styled from "styled-components";

import { useLegacyAdminNoticeDismissal } from "@hooks/admin/useLegacyAdminNoticeDismissal";
import { adminRoutePaths } from "@pages/AdminDashboard/adminRoutes";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { focusOutlineOnNavy } from "@pages/AdminDashboard/theme/focusOutline";

const { color, density, radius, typeScale } = adminTokens;
const [, spaceSm, , spaceMd] = adminTokens.spacing;
// The chosen design's strip: at least 44px tall, its parts 16px apart.
const stripMinHeight = 44;
const dismissIconSize = 14;

const Strip = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: ${spaceSm}px ${spaceMd}px;
  box-sizing: border-box;
  min-height: ${stripMinHeight}px;
  padding: 6px ${spaceMd}px;
  background-color: ${color.navy};
  color: ${color.surface};
  font-family: ${typeScale.meta.fontFamily};
  font-size: ${typeScale.meta.fontSize};
  line-height: ${typeScale.meta.lineHeight};
  text-align: center;
`;

const SwitchLink = styled(Link)`
  color: ${color.surface};
  font-family: ${typeScale.labelValue.fontFamily};
  text-decoration: underline;
  text-underline-offset: 3px;

  &:hover {
    color: ${color.sage};
  }

  &:focus-visible {
    ${focusOutlineOnNavy}
  }
`;

const DismissButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${density.minimumHitTarget}px;
  height: ${density.minimumHitTarget}px;
  padding: 0;
  border: 0;
  border-radius: ${radius.pill}px;
  background: transparent;
  color: ${color.surface};
  cursor: pointer;

  &:focus-visible {
    ${focusOutlineOnNavy}
  }
`;

interface LegacyAdminNoticeProps {
  // Called when the admin dismisses the notice, so the shell can take keyboard
  // focus before the Dismiss button is gone (SHELL-R28).
  onDismiss: () => void;
}

// The strip above every screen of the rebuilt admin that offers the legacy admin
// to an admin not yet comfortable with the rebuilt one (SHELL-R24…R28).
export const LegacyAdminNotice = ({ onDismiss }: LegacyAdminNoticeProps) => {
  const { isDismissed, dismiss } = useLegacyAdminNoticeDismissal();

  if (isDismissed) return null;

  const dismissNotice = () => {
    onDismiss();
    dismiss();
  };

  return (
    <Strip role="region" aria-label="Legacy admin notice">
      <span>
        You&apos;re on the new Runway admin. The legacy admin stays available while you settle in.
      </span>
      <SwitchLink to={adminRoutePaths.legacyAdmin}>Switch to legacy admin</SwitchLink>
      <DismissButton type="button" aria-label="Dismiss" onClick={dismissNotice}>
        <svg
          aria-hidden="true"
          width={dismissIconSize}
          height={dismissIconSize}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </DismissButton>
    </Strip>
  );
};
